/**
 * 风格画廊弹窗 — 2026-06-01
 *
 * 28 风格 + 自由发挥,按 STYLE_GROUPS 分组 sticky 标题展示,顶部搜索框按 label/blurb
 * 实时过滤。点 tile 即选并关闭。侧栏「全部风格」按钮触发。
 */
import { useMemo, useState } from "react";
import { Search, Sparkles } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { cn } from "@/shared/lib/utils";
import { StyleThumb } from "../../../ui/components/free-create/StyleThumb";
import {
  PRESET_STYLES,
  STYLE_GROUPS,
  type StyleOption,
} from "../../../ui/components/free-create/styleOptions";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** 当前选中 tone(null = 自由发挥) */
  selectedStyle: string | null;
  /** 选风格:tone 或 null(自由发挥) */
  onSelect: (tone: string | null) => void;
}

export function StyleGalleryDialog({ open, onOpenChange, selectedStyle, onSelect }: Props) {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    if (!kw) return PRESET_STYLES;
    return PRESET_STYLES.filter(
      (s) =>
        s.label.toLowerCase().includes(kw) ||
        (s.blurb || "").toLowerCase().includes(kw) ||
        (s.tone || "").toLowerCase().includes(kw)
    );
  }, [q]);

  const pick = (tone: string | null) => {
    onSelect(tone);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[82vh] max-w-4xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="space-y-2 border-b px-5 py-4">
          <DialogTitle>选择风格</DialogTitle>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="搜索风格(名称 / 关键词,如:科技、水墨、撞色)"
              className="pl-9"
              aria-label="搜索风格"
            />
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* 自由发挥(仅无搜索时显示在顶部) */}
          {!q.trim() && (
            <button
              type="button"
              onClick={() => pick(null)}
              aria-pressed={selectedStyle === null}
              className={cn(
                "mb-5 flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                selectedStyle === null
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-border/60 hover:border-primary/50"
              )}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Sparkles className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-sm font-medium text-foreground">自由发挥</span>
                <span className="block text-[13px] text-muted-foreground">
                  不指定风格,由 AI 根据你的内容与行业自动判断调性
                </span>
              </span>
            </button>
          )}

          {STYLE_GROUPS.map((group) => {
            const items = filtered.filter((s) => s.group === group.key);
            if (items.length === 0) return null;
            return (
              <section key={group.key} className="mb-6 last:mb-0">
                <div className="sticky top-0 z-10 -mx-5 mb-3 bg-background/95 px-5 py-1 backdrop-blur">
                  <h3 className="text-sm font-semibold text-foreground">
                    {group.title}
                    <span className="ml-2 text-[13px] font-normal text-muted-foreground">
                      {group.hint}
                    </span>
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                  {items.map((s: StyleOption) => (
                    <StyleThumb
                      key={s.key}
                      option={s}
                      selected={selectedStyle === s.tone}
                      onSelect={() => pick(s.tone)}
                      size="md"
                    />
                  ))}
                </div>
              </section>
            );
          })}

          {q.trim() && filtered.length === 0 && (
            <p className="py-10 text-center text-sm text-muted-foreground">
              没有匹配「{q.trim()}」的风格,换个词试试,或在面板里用「自定义」直接描述。
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
